import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const navLinkVariants = cva(
  'inline-flex cursor-pointer items-center gap-2.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25',
  {
    variants: {
      variant: {
        inline: '',
        sidebar: 'w-full',
      },
      size: {
        sm: 'type-body-sm px-2.5 py-1.5',
        md: 'type-body-sm px-3 py-2',
        lg: 'type-body px-3.5 py-2.5',
      },
    },
    compoundVariants: [
      { variant: 'inline', size: 'sm', className: 'px-0 py-0' },
      { variant: 'inline', size: 'md', className: 'px-0 py-0' },
      { variant: 'inline', size: 'lg', className: 'px-0 py-0' },
    ],
    defaultVariants: {
      variant: 'inline',
      size: 'md',
    },
  },
);

const iconSizeMap: Record<string, string> = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
};

export interface NavLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof navLinkVariants> {
  active?: boolean;
  asChild?: boolean;
  icon?: React.ReactNode;
}

const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    {
      className,
      active = false,
      asChild = false,
      icon,
      variant,
      size,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'a';
    const isSidebar = variant === 'sidebar';
    const resolvedSize = size ?? 'md';

    return (
      <Comp
        ref={ref}
        data-active={active || undefined}
        className={cn(
          navLinkVariants({ variant, size: resolvedSize }),
          isSidebar
            ? active
              ? 'bg-primary/[0.08] font-semibold text-primary'
              : 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground'
            : active
              ? 'font-semibold text-foreground'
              : 'font-medium text-muted-foreground hover:text-foreground',
          className,
        )}
        {...props}
      >
        {icon && (
          <span className={cn('shrink-0', iconSizeMap[resolvedSize])} aria-hidden="true">
            {icon}
          </span>
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
NavLink.displayName = 'NavLink';

export { NavLink, navLinkVariants };
